'use client'

import * as React from 'react'
import { Plus, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AccordionCtx {
  openId: string | null
  setOpenId: (id: string | null) => void
}
const Ctx = React.createContext<AccordionCtx | null>(null)

export function Accordion({ className, children }: React.HTMLAttributes<HTMLDivElement>) {
  const [openId, setOpenId] = React.useState<string | null>(null)
  return (
    <Ctx.Provider value={{ openId, setOpenId }}>
      <div className={cn('flex flex-col divide-y divide-border', className)}>{children}</div>
    </Ctx.Provider>
  )
}

export interface AccordionItemProps {
  id: string
  question: React.ReactNode
  children: React.ReactNode
}

export function AccordionItem({ id, question, children }: AccordionItemProps) {
  const ctx = React.useContext(Ctx)
  if (!ctx) throw new Error('AccordionItem must be used inside Accordion')
  const open = ctx.openId === id
  return (
    <div className="py-6">
      <button
        type="button"
        onClick={() => ctx.setOpenId(open ? null : id)}
        className="flex w-full items-center justify-between gap-6 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-lg font-semibold text-text-primary">{question}</span>
        <span className="shrink-0 text-text-muted transition-transform">
          {open ? <Minus size={20} /> : <Plus size={20} />}
        </span>
      </button>
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
          open ? 'grid-rows-[1fr] opacity-100 pt-4' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="text-[15px] leading-[1.75] text-text-muted">{children}</div>
        </div>
      </div>
    </div>
  )
}
